/**
 * F6 — visual-conformance comparator (pure).
 *
 * Canon: docs/library/FIGMA-VERIFIED-LIBRARY-BUILD-REQUIREMENTS-2026-07-26.md §1 F6
 * ("생성 렌더 vs Figma 원본을 parity oracle로 대조한다 … oracle 키는
 * surface-discriminated로 … F6 green만 showroom 후보가 된다. red는 명시 거절 기록.")
 *
 * WHAT THIS MODULE JUDGES — and what it deliberately does not
 * ----------------------------------------------------------
 * F6 asks: "does the generated app actually follow the source design?" The
 * canary precedent (apps/cogo-studio/e2e/helpers/current-run-visual-oracle.ts,
 * band 0.08) answers a DIFFERENT question — same app vs an approved golden of
 * that same app, i.e. a regression oracle over identical content at identical
 * dimensions. That comparator hard-throws on a dimension mismatch, and its
 * mismatch ratio is only meaningful when the two images depict the same thing.
 *
 * A Figma source frame and a generated app render depict DIFFERENT content at
 * DIFFERENT dimensions with DIFFERENT structure. Running pixelmatch across them
 * yields a ratio near 1.0 for every input, pass or fail — a fake score. So this
 * module implements the strongest defensible medium-independent comparison the
 * available source-extraction surface supports: **palette conformance**.
 *
 *   claim  : every colour the generated render puts on screen is accounted for
 *            by the source-derived palette (whose every entry is traceable to a
 *            parse-stable Figma variable key / detected-colour id in the pinned
 *            .fig), AND the source's own identifying chromatic colours are
 *            actually present, AND the render scores materially better against
 *            the source palette than against an unrelated control palette.
 *   NOT claimed: spatial fidelity, layout, typography, component geometry.
 *
 * The guarantee label is emitted with every report (see F6_GUARANTEE_SCOPE) so
 * a green result can never be read as more than it is.
 *
 * Key policy: every palette entry and oracle key is keyed by source ids /
 * Figma variable keys / structural names / content hashes only. SceneGraph node
 * ids are parse-unstable (established gotcha) and never appear here.
 *
 * This module is pure and dependency-free: PNG decoding, file and network I/O
 * live in scripts/agentic/f6-parity/.
 */

export const F6_VISUAL_CONFORMANCE_CONTRACT = "cogo.f6-visual-conformance.v1";
export const F6_VISUAL_PARITY_REPORT_SCHEMA = "cogo.f6-visual-parity-report.v1";

export const F6_GUARANTEE_SCOPE =
  "palette-conformance-verified; spatial-fidelity-NOT-verified; typography-NOT-verified; layout-NOT-verified";

/* ------------------------------------------------------------------ *
 * Colour primitives
 * ------------------------------------------------------------------ */

export interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

export interface Lab {
  readonly l: number;
  readonly a: number;
  readonly b: number;
}

const HEX3 = /^#?([0-9a-f]{3})$/i;
const HEX4 = /^#?([0-9a-f]{4})$/i;
const HEX6 = /^#?([0-9a-f]{6})$/i;
const HEX8 = /^#?([0-9a-f]{8})$/i;

/**
 * CSS Color 4 §5.2 shorthand expansion: one digit becomes a full byte by
 * being duplicated (`a` → `aa`). `0xa * 0x11 === 0xaa`, so this is the exact
 * expansion, not an approximation — shorthand is losslessly widenable.
 */
function expandNibble(digit: string): number {
  return Number.parseInt(digit, 16) * 0x11;
}

/** Parses `#rgb` shorthand (`#abc` → `#aabbcc`); null for any other width. */
export function parseHex3(value: string): Rgb | null {
  const match = HEX3.exec(value.trim());
  if (!match) return null;
  const digits = match[1]!;
  return {
    r: expandNibble(digits[0]!),
    g: expandNibble(digits[1]!),
    b: expandNibble(digits[2]!),
  };
}

/** Parses `#rgba` shorthand (`#abcd` → `#aabbccdd`); null for any other width. */
export function parseHex4(
  value: string,
): { rgb: Rgb; alpha: number } | null {
  const match = HEX4.exec(value.trim());
  if (!match) return null;
  const digits = match[1]!;
  return {
    rgb: {
      r: expandNibble(digits[0]!),
      g: expandNibble(digits[1]!),
      b: expandNibble(digits[2]!),
    },
    alpha: expandNibble(digits[3]!) / 255,
  };
}

/** Parses `#rrggbb`; returns null for anything else (including 8-digit hex). */
export function parseHex6(value: string): Rgb | null {
  const match = HEX6.exec(value.trim());
  if (!match) return null;
  const int = Number.parseInt(match[1]!, 16);
  return { r: (int >> 16) & 0xff, g: (int >> 8) & 0xff, b: int & 0xff };
}

/** Parses `#rrggbbaa`; returns null for anything else. */
export function parseHex8(
  value: string,
): { rgb: Rgb; alpha: number } | null {
  const match = HEX8.exec(value.trim());
  if (!match) return null;
  const int = Number.parseInt(match[1]!, 16);
  return {
    rgb: {
      r: (int >>> 24) & 0xff,
      g: (int >>> 16) & 0xff,
      b: (int >>> 8) & 0xff,
    },
    alpha: (int & 0xff) / 255,
  };
}

export function formatHex6(rgb: Rgb): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${[clamp(rgb.r), clamp(rgb.g), clamp(rgb.b)]
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("")}`;
}

/** Source-over composite of a translucent colour onto an opaque backdrop. */
export function compositeOver(fg: Rgb, alpha: number, backdrop: Rgb): Rgb {
  const a = Math.max(0, Math.min(1, alpha));
  return {
    r: fg.r * a + backdrop.r * (1 - a),
    g: fg.g * a + backdrop.g * (1 - a),
    b: fg.b * a + backdrop.b * (1 - a),
  };
}

const OKLCH = /^oklch\(\s*([0-9.]+%?)\s+([0-9.]+%?)\s+([0-9.-]+)(?:deg)?\s*(?:\/.*)?\)$/i;

function linearToSrgbByte(channel: number): number {
  const clamped = Math.max(0, Math.min(1, channel));
  const encoded =
    clamped <= 0.0031308
      ? clamped * 12.92
      : 1.055 * clamped ** (1 / 2.4) - 0.055;
  return Math.round(encoded * 255);
}

/**
 * `oklch(L C H)` → sRGB. Needed because the repo's default preset table
 * (`@cogo/design-presets` BASE_COLORS) states its colours in oklch; the F6
 * control palette is read from that table rather than hand-transcribed, so the
 * discrimination margin is measured against the real default, not a guess.
 */
export function parseOklch(value: string): Rgb | null {
  const match = OKLCH.exec(value.trim());
  if (!match) return null;
  const num = (raw: string, percentScale: number) =>
    raw.endsWith("%")
      ? Number.parseFloat(raw.slice(0, -1)) * percentScale
      : Number.parseFloat(raw);
  const lightness = num(match[1]!, 0.01);
  const chroma = num(match[2]!, 0.004);
  const hue = Number.parseFloat(match[3]!);
  if (!Number.isFinite(lightness) || !Number.isFinite(chroma) || !Number.isFinite(hue)) {
    return null;
  }
  const radians = (hue * Math.PI) / 180;
  const a = chroma * Math.cos(radians);
  const b = chroma * Math.sin(radians);
  const l = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return {
    r: linearToSrgbByte(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: linearToSrgbByte(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: linearToSrgbByte(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  };
}

/**
 * Accepts `#rrggbb`, `#rrggbbaa`, `#rgb`, `#rgba` (alpha dropped in both
 * alpha-bearing forms) or `oklch(...)`.
 *
 * Shorthand is in the chain because the upstream data model that decides what
 * counts as a colour at all — `SourceTokenRow.swatch`, gated by
 * `/^#[0-9a-f]{3,8}$/i` in
 * apps/cogo-studio/src/features/openpencil-figma/openpencil-source-library-model.ts
 * — admits 3- and 4-digit values. Without these branches a value that model
 * labels "a renderable colour" parses to null downstream, which surfaces to the
 * user as an unresolvable axis (`derive-axes.ts` → `origin: 'gap'`) for a
 * colour that is in fact fully determined.
 *
 * Widths CSS does not define (5, 7) stay unparseable on purpose: they are not
 * shorthand for anything, so any expansion would be invented rather than read.
 *
 * The fixed-width forms are tried first — they are what the Figma extractor
 * actually emits (`fill.ts` pads every channel to two digits), so the common
 * case does not pay for the shorthand branches. The four regexes are mutually
 * exclusive by digit count, so order is a cost choice, not a semantic one.
 */
export function parseColorToRgb(value: string): Rgb | null {
  return (
    parseHex6(value) ??
    parseHex8(value)?.rgb ??
    parseHex3(value) ??
    parseHex4(value)?.rgb ??
    parseOklch(value) ??
    null
  );
}

const D65 = { x: 0.95047, y: 1.0, z: 1.08883 } as const;
const LAB_DELTA = 6 / 29;

function srgbToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function labF(t: number): number {
  return t > LAB_DELTA ** 3
    ? Math.cbrt(t)
    : t / (3 * LAB_DELTA ** 2) + 4 / 29;
}

/** sRGB (D65) → CIE L*a*b*. Deterministic; no lookup tables. */
export function rgbToLab(rgb: Rgb): Lab {
  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);
  const x = (0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / D65.x;
  const y = (0.2126729 * r + 0.7151522 * g + 0.072175 * b) / D65.y;
  const z = (0.0193339 * r + 0.119192 * g + 0.9503041 * b) / D65.z;
  const fx = labF(x);
  const fy = labF(y);
  const fz = labF(z);
  return { l: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
}

/** CIE76 ΔE. Chosen over ΔE2000 because it is trivially auditable. */
export function deltaE76(a: Lab, b: Lab): number {
  return Math.hypot(a.l - b.l, a.a - b.a, a.b - b.b);
}

/** CIE L*a*b* chroma C* — the achromatic/chromatic discriminator. */
export function labChroma(lab: Lab): number {
  return Math.hypot(lab.a, lab.b);
}

/**
 * ΔE from `point` to the anti-aliasing blend locus between two palette colours.
 *
 * Compositing and font anti-aliasing interpolate LINEARLY IN THE FRAMEBUFFER'S
 * sRGB BYTES, not in Lab. A straight Lab segment is the wrong locus: the true
 * 50% blend of #ffffff and #2589f4 sits 8.3 ΔE off it, which would read as
 * palette drift at any sane tolerance. So the nearest point is found by
 * projection in sRGB (where the blend is a straight line) and the residual is
 * then measured perceptually in Lab.
 */
export function deltaEToBlendLocus(point: Rgb, from: Rgb, to: Rgb): number {
  const dr = to.r - from.r;
  const dg = to.g - from.g;
  const db = to.b - from.b;
  const lenSq = dr * dr + dg * dg + db * db;
  if (lenSq === 0) return deltaE76(rgbToLab(point), rgbToLab(from));
  let t =
    ((point.r - from.r) * dr +
      (point.g - from.g) * dg +
      (point.b - from.b) * db) /
    lenSq;
  t = Math.max(0, Math.min(1, t));
  return deltaE76(
    rgbToLab(point),
    rgbToLab({
      r: from.r + t * dr,
      g: from.g + t * dg,
      b: from.b + t * db,
    }),
  );
}

/* ------------------------------------------------------------------ *
 * Source palette
 * ------------------------------------------------------------------ */

export type F6PaletteOrigin =
  | "detected-color"
  | "figma-variable"
  | "pack-token"
  | "alpha-composite";

export interface F6PaletteEntry {
  /** Opaque `#rrggbb`. */
  readonly hex: string;
  /**
   * Relative prominence in the source. `detected-color` entries carry the
   * readback usage count; declared entries carry a nominal weight so they never
   * dominate the brand set purely by being declared.
   */
  readonly weight: number;
  readonly origin: F6PaletteOrigin;
  /** Parse-stable provenance (figma variable key / token id / colour id). */
  readonly ref: string;
}

export interface F6SourcePalette {
  readonly paletteId: string;
  /** Content-addressed pin of the source (`cogo-source://openpencil-file/<sha256>`). */
  readonly sourceArtifactRef: string;
  readonly entries: readonly F6PaletteEntry[];
  /** 8-digit-hex entries that could not be composited, recorded not dropped. */
  readonly alphaExcluded: readonly { value: string; ref: string }[];
}

export interface BuildPaletteOptions {
  /** Opaque backdrops used to composite 8-digit-hex tokens. Default `["#ffffff"]`. */
  readonly compositeBackdrops?: readonly string[];
  /** Nominal weight for declared (non usage-counted) entries. Default 1. */
  readonly declaredWeight?: number;
}

interface ReadbackColorSample {
  value?: unknown;
  usageCount?: unknown;
  id?: unknown;
}

/**
 * F1 `analyzeColors.samples` → palette entries.
 *
 * These are document-level detected colours with usage counts — the source's
 * as-authored colour evidence. Preferred over a histogram of the OpenPencil
 * raster preview, which drops instance fills (see F6 doc, "source raster is not
 * a palette oracle").
 */
export function buildPaletteFromReadbackColors(
  samples: readonly unknown[],
): F6PaletteEntry[] {
  const entries: F6PaletteEntry[] = [];
  for (const raw of samples) {
    const sample = raw as ReadbackColorSample;
    if (typeof sample?.value !== "string") continue;
    const rgb = parseHex6(sample.value);
    if (!rgb) continue;
    const usage =
      typeof sample.usageCount === "number" && Number.isFinite(sample.usageCount)
        ? Math.max(0, sample.usageCount)
        : 0;
    entries.push({
      hex: formatHex6(rgb),
      weight: usage,
      origin: "detected-color",
      ref:
        typeof sample.id === "string" && sample.id.length > 0
          ? `readback-color:${sample.id}`
          : `readback-color:${formatHex6(rgb)}`,
    });
  }
  return entries;
}

interface PackTokenEntry {
  tokenId?: unknown;
  tokenType?: unknown;
  value?: unknown;
  mode?: unknown;
}

/**
 * F2/F2-rev design-system pack tokens → palette entries.
 *
 * 8-digit-hex tokens (the alpha-flagged `border`/`input` slots) have no fixed
 * rendered colour; each is composited over every configured backdrop and the
 * results recorded as `alpha-composite` entries. Tokens that cannot be parsed
 * at all land in `alphaExcluded` rather than being silently dropped (F2 rule).
 */
export function buildPaletteFromPackTokens(
  tokens: readonly unknown[],
  options: BuildPaletteOptions = {},
): { entries: F6PaletteEntry[]; alphaExcluded: { value: string; ref: string }[] } {
  const declaredWeight = options.declaredWeight ?? 1;
  const backdrops = (options.compositeBackdrops ?? ["#ffffff"])
    .map((value) => parseHex6(value))
    .filter((value): value is Rgb => value !== null);
  const entries: F6PaletteEntry[] = [];
  const alphaExcluded: { value: string; ref: string }[] = [];

  for (const raw of tokens) {
    const token = raw as PackTokenEntry;
    if (typeof token?.value !== "string") continue;
    if (token.tokenType !== undefined && token.tokenType !== "color") continue;
    const tokenId =
      typeof token.tokenId === "string" ? token.tokenId : "pack-token:unknown";
    const mode = typeof token.mode === "string" ? token.mode : "unspecified";
    const ref = `pack-token:${tokenId}@${mode}`;

    const opaque = parseHex6(token.value);
    if (opaque) {
      entries.push({
        hex: formatHex6(opaque),
        weight: declaredWeight,
        origin: "pack-token",
        ref,
      });
      continue;
    }

    const translucent = parseHex8(token.value);
    if (!translucent) {
      alphaExcluded.push({ value: token.value, ref });
      continue;
    }
    if (backdrops.length === 0) {
      alphaExcluded.push({ value: token.value, ref });
      continue;
    }
    for (const backdrop of backdrops) {
      entries.push({
        hex: formatHex6(
          compositeOver(translucent.rgb, translucent.alpha, backdrop),
        ),
        weight: declaredWeight,
        origin: "alpha-composite",
        ref: `${ref}|over:${formatHex6(backdrop)}`,
      });
    }
  }
  return { entries, alphaExcluded };
}

/**
 * CSS theme-variable map (`@cogo/design-presets` `ThemeVars`) → palette entries.
 *
 * Used to build the CONTROL palette from the repo's own default preset table.
 * Non-colour vars (`radius: '0.625rem'`) are skipped by parse failure; every
 * skip is returned so the control side is auditable too.
 */
export function buildPaletteFromThemeVars(
  vars: Readonly<Record<string, string>>,
  options: { mode?: string; declaredWeight?: number } = {},
): { entries: F6PaletteEntry[]; skipped: { name: string; value: string }[] } {
  const mode = options.mode ?? "light";
  const declaredWeight = options.declaredWeight ?? 1;
  const entries: F6PaletteEntry[] = [];
  const skipped: { name: string; value: string }[] = [];
  for (const [name, value] of Object.entries(vars)) {
    const rgb = parseColorToRgb(value);
    if (!rgb) {
      skipped.push({ name, value });
      continue;
    }
    entries.push({
      hex: formatHex6(rgb),
      weight: declaredWeight,
      origin: "pack-token",
      ref: `theme-var:${name}@${mode}`,
    });
  }
  return { entries, skipped };
}

/** `#rrggbb` → as-authored usage count, from F1 `analyzeColors.samples`. */
export function readbackUsageByHex(
  samples: readonly unknown[],
): Map<string, number> {
  const usage = new Map<string, number>();
  for (const entry of buildPaletteFromReadbackColors(samples)) {
    usage.set(entry.hex, Math.max(usage.get(entry.hex) ?? 0, entry.weight));
  }
  return usage;
}

/**
 * Re-weights declared palette entries by their as-authored usage in the source.
 *
 * Membership stays contractual (the slots the generated app is bound to) while
 * the brand set stays usage-driven: without this, every declared token has the
 * same nominal weight and `selectBrandEntries` would pick arbitrarily.
 * Unmatched entries keep `fallbackWeight` — they are declared but never drawn.
 */
export function applyUsageWeights(
  entries: readonly F6PaletteEntry[],
  usageByHex: ReadonlyMap<string, number>,
  fallbackWeight = 0,
): F6PaletteEntry[] {
  return entries.map((entry) => ({
    ...entry,
    weight: usageByHex.get(entry.hex.toLowerCase()) ?? fallbackWeight,
  }));
}

/**
 * Merges entry lists, deduplicating by hex. The surviving entry keeps the
 * summed weight and the provenance of the highest-weight contributor, so the
 * brand set stays usage-driven rather than declaration-driven.
 */
export function mergePaletteEntries(
  ...lists: readonly (readonly F6PaletteEntry[])[]
): F6PaletteEntry[] {
  const byHex = new Map<string, F6PaletteEntry>();
  for (const list of lists) {
    for (const entry of list) {
      const key = entry.hex.toLowerCase();
      const existing = byHex.get(key);
      if (!existing) {
        byHex.set(key, { ...entry, hex: key });
        continue;
      }
      const dominant = entry.weight > existing.weight ? entry : existing;
      byHex.set(key, {
        hex: key,
        weight: existing.weight + entry.weight,
        origin: dominant.origin,
        ref: dominant.ref,
      });
    }
  }
  return [...byHex.values()].sort(
    (a, b) => b.weight - a.weight || a.hex.localeCompare(b.hex),
  );
}

/* ------------------------------------------------------------------ *
 * Render side — pixel histogram
 * ------------------------------------------------------------------ */

export interface F6PixelHistogram {
  readonly width: number;
  readonly height: number;
  readonly totalPixels: number;
  /** Pixels at or above `minAlpha`; the only ones scored. */
  readonly opaquePixels: number;
  /** `#rrggbb` → opaque pixel count. */
  readonly counts: ReadonlyMap<string, number>;
}

export interface BuildHistogramOptions {
  /** 0–255. Pixels below this alpha are excluded entirely. Default 250. */
  readonly minAlpha?: number;
}

/** RGBA byte buffer (pngjs `PNG.data` layout) → histogram. Pure. */
export function buildPixelHistogram(
  data: ArrayLike<number>,
  width: number,
  height: number,
  options: BuildHistogramOptions = {},
): F6PixelHistogram {
  const minAlpha = options.minAlpha ?? 250;
  const totalPixels = width * height;
  if (data.length < totalPixels * 4) {
    throw new Error(
      `F6 histogram: RGBA buffer too small (${data.length} bytes for ${width}x${height}).`,
    );
  }
  const counts = new Map<string, number>();
  let opaquePixels = 0;
  for (let index = 0; index < totalPixels; index += 1) {
    const offset = index * 4;
    if (data[offset + 3]! < minAlpha) continue;
    opaquePixels += 1;
    const hex = formatHex6({
      r: data[offset]!,
      g: data[offset + 1]!,
      b: data[offset + 2]!,
    });
    counts.set(hex, (counts.get(hex) ?? 0) + 1);
  }
  return { width, height, totalPixels, opaquePixels, counts };
}

/* ------------------------------------------------------------------ *
 * Oracle key — surface-discriminated (canon §1 F6)
 * ------------------------------------------------------------------ */

/**
 * Hard discriminator, not a fuzzy-match hint — mirrors `VisualOracleSurface` in
 * the canary oracle. A generated screenshot is never compared under the same
 * key as a Figma source raster.
 */
export type F6RenderSurface =
  | "figma-source-raster"
  | "generated-screenshot"
  | "studio-preview"
  | "control-render";

export interface F6OracleKey {
  /** Content-addressed .fig pin — the source side of the parity claim. */
  readonly sourceArtifactRef: string;
  readonly paletteId: string;
  /** Structural surface id (route / frame name path) — never a SceneGraph node id. */
  readonly surfaceId: string;
  readonly viewport: { readonly width: number; readonly height: number };
  readonly renderSurface: F6RenderSurface;
}

export function formatF6OracleKey(key: F6OracleKey): string {
  return [
    key.sourceArtifactRef,
    key.paletteId,
    key.renderSurface,
    key.surfaceId,
    `${key.viewport.width}x${key.viewport.height}`,
  ].join("|");
}

export function f6OracleKeysEqual(a: F6OracleKey, b: F6OracleKey): boolean {
  return formatF6OracleKey(a) === formatF6OracleKey(b);
}

/* ------------------------------------------------------------------ *
 * Conformance scoring
 * ------------------------------------------------------------------ */

export interface F6ConformanceOptions {
  /**
   * ΔE76 tolerance for palette membership. Default 6 — above the ~2.3 JND so
   * PNG quantisation and sub-pixel shading do not read as drift, below the
   * ~11 ΔE separating the Woori blue from the shadcn default primary.
   */
  readonly deltaETolerance?: number;
  /**
   * Lab C* at or above which a pixel counts as chromatic. Default 6.
   *
   * Calibrated, not guessed: the shadcn default neutral ramps sit at C* ≤ 3
   * (zinc-900 #18181b = 2.25), while the Woori source's cool-grey ramp — which
   * is brand-carrying — sits at C* 5–12 (#6f7d8c = 9.99, #8295a7 = 11.99). A
   * threshold of 6 keeps the cool greys inside the judged population while
   * still excluding true neutrals.
   */
  readonly chromaThreshold?: number;
  /**
   * Top-weight palette entries considered as blend endpoints for
   * anti-aliasing attribution. Default 24. 0 disables blend attribution.
   */
  readonly blendPairLimit?: number;
  /**
   * Require at least one blend endpoint to be achromatic. Default true.
   *
   * Real anti-aliasing and translucency in these UIs happen against surfaces —
   * text on background, border on card. A blend between two saturated brand
   * colours is not an AA artifact of a token pair, it is an unexplained colour,
   * and admitting it lets a large palette explain nearly any render.
   */
  readonly blendRequiresNeutralEndpoint?: boolean;
  /** Palette entries forming the brand-recall expectation set. Default 8. */
  readonly brandEntryCount?: number;
  /** Minimum render coverage for a brand colour to count as observed. Default 0.0002. */
  readonly brandObservedMinCoverage?: number;
  /** Non-conforming colours listed in the report. Default 12. */
  readonly reportedNonConformingColors?: number;
}

export interface F6NonConformingColor {
  readonly hex: string;
  readonly coverage: number;
  readonly nearestPaletteHex: string | null;
  readonly nearestPaletteRef: string | null;
  readonly deltaE: number;
}

export interface F6BrandRecall {
  readonly expected: readonly { hex: string; ref: string; weight: number }[];
  readonly observed: readonly string[];
  readonly missing: readonly string[];
  readonly recall: number;
}

export interface F6SurfaceConformance {
  readonly contract: typeof F6_VISUAL_CONFORMANCE_CONTRACT;
  readonly oracleKey: F6OracleKey;
  readonly oracleKeyString: string;
  readonly paletteEntryCount: number;
  readonly totalPixels: number;
  readonly opaquePixels: number;
  readonly chromaticPixels: number;
  readonly chromaticFraction: number;
  /** Conformant share of chromatic pixels — the gated metric. */
  readonly chromaticConformance: number;
  /** Reported, never gated: white/near-grey conforms against almost any palette. */
  readonly achromaticConformance: number;
  readonly overallConformance: number;
  /** Share of conformant chromatic pixels matched to an entry directly. */
  readonly directMemberFraction: number;
  /** …and the share attributed to an anti-aliasing blend between two entries. */
  readonly blendAttributedFraction: number;
  readonly brandRecall: F6BrandRecall;
  readonly topNonConformingColors: readonly F6NonConformingColor[];
  readonly options: Required<F6ConformanceOptions>;
}

const DEFAULT_CONFORMANCE_OPTIONS: Required<F6ConformanceOptions> = {
  deltaETolerance: 6,
  chromaThreshold: 6,
  blendPairLimit: 24,
  blendRequiresNeutralEndpoint: true,
  brandEntryCount: 8,
  brandObservedMinCoverage: 0.0002,
  reportedNonConformingColors: 12,
};

interface PreparedPalette {
  readonly entries: readonly F6PaletteEntry[];
  readonly rgbs: readonly Rgb[];
  readonly labs: readonly Lab[];
  readonly blendIndices: readonly number[];
  readonly neutral: readonly boolean[];
}

function preparePalette(
  palette: F6SourcePalette,
  blendPairLimit: number,
  chromaThreshold: number,
): PreparedPalette {
  const entries = palette.entries;
  const rgbs = entries.map((entry) => {
    const rgb = parseHex6(entry.hex);
    if (!rgb) {
      throw new Error(`F6 palette entry is not opaque hex: ${entry.hex}`);
    }
    return rgb;
  });
  const labs = rgbs.map((rgb) => rgbToLab(rgb));
  const neutral = labs.map((lab) => labChroma(lab) < chromaThreshold);
  const blendIndices = entries
    .map((entry, index) => ({ index, weight: entry.weight }))
    .sort((a, b) => b.weight - a.weight || a.index - b.index)
    .slice(0, Math.max(0, blendPairLimit))
    .map((item) => item.index);
  return { entries, rgbs, labs, blendIndices, neutral };
}

/**
 * Brand set = the highest-weight CHROMATIC palette entries.
 *
 * Achromatic entries are excluded because white/black/grey are shared by every
 * design system — they carry no brand identity and would make recall vacuous.
 */
export function selectBrandEntries(
  palette: F6SourcePalette,
  options: F6ConformanceOptions = {},
): { hex: string; ref: string; weight: number }[] {
  const chromaThreshold =
    options.chromaThreshold ?? DEFAULT_CONFORMANCE_OPTIONS.chromaThreshold;
  const count =
    options.brandEntryCount ?? DEFAULT_CONFORMANCE_OPTIONS.brandEntryCount;
  return palette.entries
    .filter((entry) => {
      const rgb = parseHex6(entry.hex);
      return rgb !== null && labChroma(rgbToLab(rgb)) >= chromaThreshold;
    })
    .sort((a, b) => b.weight - a.weight || a.hex.localeCompare(b.hex))
    .slice(0, Math.max(0, count))
    .map((entry) => ({ hex: entry.hex, ref: entry.ref, weight: entry.weight }));
}

export function scoreF6SurfaceConformance(input: {
  oracleKey: F6OracleKey;
  palette: F6SourcePalette;
  histogram: F6PixelHistogram;
  options?: F6ConformanceOptions;
}): F6SurfaceConformance {
  const options: Required<F6ConformanceOptions> = {
    ...DEFAULT_CONFORMANCE_OPTIONS,
    ...input.options,
  };
  const prepared = preparePalette(
    input.palette,
    options.blendPairLimit,
    options.chromaThreshold,
  );
  const { histogram } = input;

  let chromaticPixels = 0;
  let chromaticConformant = 0;
  let chromaticDirect = 0;
  let chromaticBlend = 0;
  let achromaticPixels = 0;
  let achromaticConformant = 0;

  const brandExpected = selectBrandEntries(input.palette, options);
  const brandLabs = brandExpected.map((entry) => ({
    hex: entry.hex,
    lab: rgbToLab(parseHex6(entry.hex)!),
    coverage: 0,
  }));

  const nonConforming: F6NonConformingColor[] = [];
  const denominator = histogram.opaquePixels || 1;

  for (const [hex, count] of histogram.counts) {
    const rgb = parseHex6(hex);
    if (!rgb) continue;
    const lab = rgbToLab(rgb);
    const chromatic = labChroma(lab) >= options.chromaThreshold;

    let nearest = Number.POSITIVE_INFINITY;
    let nearestIndex = -1;
    for (let index = 0; index < prepared.labs.length; index += 1) {
      const distance = deltaE76(lab, prepared.labs[index]!);
      if (distance < nearest) {
        nearest = distance;
        nearestIndex = index;
      }
      if (nearest === 0) break;
    }

    const direct = nearest <= options.deltaETolerance;
    let blended = false;
    if (!direct && prepared.blendIndices.length > 1) {
      outer: for (let i = 0; i < prepared.blendIndices.length; i += 1) {
        const fromIndex = prepared.blendIndices[i]!;
        const from = prepared.rgbs[fromIndex]!;
        for (let j = i + 1; j < prepared.blendIndices.length; j += 1) {
          const toIndex = prepared.blendIndices[j]!;
          if (
            options.blendRequiresNeutralEndpoint &&
            !prepared.neutral[fromIndex] &&
            !prepared.neutral[toIndex]
          ) {
            continue;
          }
          const to = prepared.rgbs[toIndex]!;
          if (deltaEToBlendLocus(rgb, from, to) <= options.deltaETolerance) {
            blended = true;
            break outer;
          }
        }
      }
    }

    if (chromatic) {
      chromaticPixels += count;
      if (direct) {
        chromaticConformant += count;
        chromaticDirect += count;
      } else if (blended) {
        chromaticConformant += count;
        chromaticBlend += count;
      }
    } else {
      achromaticPixels += count;
      if (direct || blended) achromaticConformant += count;
    }

    if (!direct && !blended) {
      nonConforming.push({
        hex,
        coverage: count / denominator,
        nearestPaletteHex:
          nearestIndex >= 0 ? prepared.entries[nearestIndex]!.hex : null,
        nearestPaletteRef:
          nearestIndex >= 0 ? prepared.entries[nearestIndex]!.ref : null,
        deltaE: Number.isFinite(nearest) ? nearest : Number.NaN,
      });
    }

    for (const brand of brandLabs) {
      if (deltaE76(lab, brand.lab) <= options.deltaETolerance) {
        brand.coverage += count / denominator;
      }
    }
  }

  const observed = brandLabs
    .filter((brand) => brand.coverage >= options.brandObservedMinCoverage)
    .map((brand) => brand.hex);
  const missing = brandLabs
    .filter((brand) => brand.coverage < options.brandObservedMinCoverage)
    .map((brand) => brand.hex);

  nonConforming.sort((a, b) => b.coverage - a.coverage);

  return {
    contract: F6_VISUAL_CONFORMANCE_CONTRACT,
    oracleKey: input.oracleKey,
    oracleKeyString: formatF6OracleKey(input.oracleKey),
    paletteEntryCount: prepared.entries.length,
    totalPixels: histogram.totalPixels,
    opaquePixels: histogram.opaquePixels,
    chromaticPixels,
    chromaticFraction: chromaticPixels / denominator,
    chromaticConformance:
      chromaticPixels === 0 ? 0 : chromaticConformant / chromaticPixels,
    achromaticConformance:
      achromaticPixels === 0 ? 0 : achromaticConformant / achromaticPixels,
    overallConformance:
      (chromaticConformant + achromaticConformant) / denominator,
    directMemberFraction:
      chromaticConformant === 0 ? 0 : chromaticDirect / chromaticConformant,
    blendAttributedFraction:
      chromaticConformant === 0 ? 0 : chromaticBlend / chromaticConformant,
    brandRecall: {
      expected: brandExpected,
      observed,
      missing,
      recall:
        brandExpected.length === 0 ? 0 : observed.length / brandExpected.length,
    },
    topNonConformingColors: nonConforming.slice(
      0,
      Math.max(0, options.reportedNonConformingColors),
    ),
    options,
  };
}

/* ------------------------------------------------------------------ *
 * Verdict
 * ------------------------------------------------------------------ */

export type F6Verdict = "pass" | "fail" | "indeterminate";

export interface F6Threshold {
  /** Minimum chromatic-pixel conformance against the source palette. */
  readonly minChromaticConformance: number;
  /** Minimum share of the source's identifying chromatic colours present. */
  readonly minBrandRecall: number;
  /**
   * Minimum `chromaticConformance(source) − chromaticConformance(control)`.
   * Guards the vacuous pass: a render that is generically neutral scores well
   * against every palette and must not be credited to the source design.
   */
  readonly minDiscriminationMargin: number;
  /**
   * Below this chromatic fraction the render carries too little colour to
   * judge — the verdict is `indeterminate`, never `pass`.
   */
  readonly minChromaticFraction: number;
  /** Pointer to the calibration evidence that set these numbers. */
  readonly calibrationRef: string;
}

export interface F6SurfaceVerdict {
  readonly oracleKey: F6OracleKey;
  readonly oracleKeyString: string;
  readonly verdict: F6Verdict;
  /** Every unmet condition — a red result names its reasons. */
  readonly reasons: readonly string[];
  readonly chromaticConformance: number;
  readonly controlChromaticConformance: number | null;
  readonly discriminationMargin: number | null;
  readonly brandRecall: number;
  readonly chromaticFraction: number;
  readonly threshold: F6Threshold;
}

export function judgeF6Surface(input: {
  conformance: F6SurfaceConformance;
  controlConformance?: F6SurfaceConformance | null;
  threshold: F6Threshold;
}): F6SurfaceVerdict {
  const { conformance, threshold } = input;
  const control = input.controlConformance ?? null;
  const margin =
    control === null
      ? null
      : conformance.chromaticConformance - control.chromaticConformance;
  const reasons: string[] = [];

  if (conformance.chromaticFraction < threshold.minChromaticFraction) {
    return {
      oracleKey: conformance.oracleKey,
      oracleKeyString: conformance.oracleKeyString,
      verdict: "indeterminate",
      reasons: [
        `chromaticFraction ${conformance.chromaticFraction.toFixed(4)} < minChromaticFraction ${threshold.minChromaticFraction} — render carries too little colour to judge`,
      ],
      chromaticConformance: conformance.chromaticConformance,
      controlChromaticConformance: control?.chromaticConformance ?? null,
      discriminationMargin: margin,
      brandRecall: conformance.brandRecall.recall,
      chromaticFraction: conformance.chromaticFraction,
      threshold,
    };
  }

  if (conformance.chromaticConformance < threshold.minChromaticConformance) {
    reasons.push(
      `chromaticConformance ${conformance.chromaticConformance.toFixed(4)} < ${threshold.minChromaticConformance}`,
    );
  }
  if (conformance.brandRecall.recall < threshold.minBrandRecall) {
    reasons.push(
      `brandRecall ${conformance.brandRecall.recall.toFixed(4)} < ${threshold.minBrandRecall} (missing ${conformance.brandRecall.missing.join(", ") || "none"})`,
    );
  }
  if (threshold.minDiscriminationMargin > 0) {
    if (margin === null) {
      reasons.push(
        "discriminationMargin unavailable — no control palette scored, but the threshold requires one",
      );
    } else if (margin < threshold.minDiscriminationMargin) {
      reasons.push(
        `discriminationMargin ${margin.toFixed(4)} < ${threshold.minDiscriminationMargin}`,
      );
    }
  }

  return {
    oracleKey: conformance.oracleKey,
    oracleKeyString: conformance.oracleKeyString,
    verdict: reasons.length === 0 ? "pass" : "fail",
    reasons,
    chromaticConformance: conformance.chromaticConformance,
    controlChromaticConformance: control?.chromaticConformance ?? null,
    discriminationMargin: margin,
    brandRecall: conformance.brandRecall.recall,
    chromaticFraction: conformance.chromaticFraction,
    threshold,
  };
}

/* ------------------------------------------------------------------ *
 * Report
 * ------------------------------------------------------------------ */

export interface F6ParityReport {
  readonly schemaVersion: typeof F6_VISUAL_PARITY_REPORT_SCHEMA;
  readonly pipelineStage: "F6";
  readonly canonDoc: string;
  readonly generatedAt: string;
  readonly guaranteeScope: typeof F6_GUARANTEE_SCOPE;
  readonly sourceArtifactRef: string;
  readonly paletteId: string;
  readonly controlPaletteId: string | null;
  readonly threshold: F6Threshold;
  readonly method: {
    readonly comparison: "palette-conformance";
    readonly metric: readonly string[];
    readonly notComputed: readonly string[];
  };
  readonly surfaces: readonly {
    readonly conformance: F6SurfaceConformance;
    readonly verdict: F6SurfaceVerdict;
  }[];
  readonly summary: {
    readonly total: number;
    readonly passed: number;
    readonly failed: number;
    readonly indeterminate: number;
    /** Enumerated rejection — canon: "red는 명시 거절 기록". */
    readonly rejectedSurfaces: readonly {
      readonly oracleKeyString: string;
      readonly surfaceId: string;
      readonly verdict: F6Verdict;
      readonly reasons: readonly string[];
    }[];
    readonly showroomCandidacy: "granted" | "denied";
  };
}

export const F6_METHOD_METRICS = [
  "chromaticConformance (ΔE76 membership in the source-derived palette, coverage-weighted over chromatic pixels)",
  "brandRecall (presence of the source's top-weight chromatic colours)",
  "discriminationMargin (chromaticConformance vs an unrelated control palette)",
] as const;

export const F6_METHOD_NOT_COMPUTED = [
  "pixel-level spatial diff against the Figma frame (different content, structure and dimensions — a mismatch ratio here is not a fidelity signal)",
  "typography scale conformance (openpencil-cli-readback.v2 exposes no type ramp for this source — recorded in the F2 report)",
  "spacing / radius conformance (same extraction-surface limit)",
  "component geometry or layout correspondence",
] as const;

export function buildF6ParityReport(input: {
  generatedAt: string;
  canonDoc: string;
  sourceArtifactRef: string;
  paletteId: string;
  controlPaletteId: string | null;
  threshold: F6Threshold;
  surfaces: readonly {
    conformance: F6SurfaceConformance;
    verdict: F6SurfaceVerdict;
  }[];
}): F6ParityReport {
  const passed = input.surfaces.filter(
    (item) => item.verdict.verdict === "pass",
  ).length;
  const failed = input.surfaces.filter(
    (item) => item.verdict.verdict === "fail",
  ).length;
  const indeterminate = input.surfaces.filter(
    (item) => item.verdict.verdict === "indeterminate",
  ).length;
  const rejectedSurfaces = input.surfaces
    .filter((item) => item.verdict.verdict !== "pass")
    .map((item) => ({
      oracleKeyString: item.verdict.oracleKeyString,
      surfaceId: item.verdict.oracleKey.surfaceId,
      verdict: item.verdict.verdict,
      reasons: item.verdict.reasons,
    }));

  return {
    schemaVersion: F6_VISUAL_PARITY_REPORT_SCHEMA,
    pipelineStage: "F6",
    canonDoc: input.canonDoc,
    generatedAt: input.generatedAt,
    guaranteeScope: F6_GUARANTEE_SCOPE,
    sourceArtifactRef: input.sourceArtifactRef,
    paletteId: input.paletteId,
    controlPaletteId: input.controlPaletteId,
    threshold: input.threshold,
    method: {
      comparison: "palette-conformance",
      metric: F6_METHOD_METRICS,
      notComputed: F6_METHOD_NOT_COMPUTED,
    },
    surfaces: input.surfaces,
    summary: {
      total: input.surfaces.length,
      passed,
      failed,
      indeterminate,
      rejectedSurfaces,
      // Canon: "F6 green만 showroom 후보가 된다." Any non-pass surface denies
      // candidacy for the whole slice — fail-closed, and `indeterminate`
      // counts as not-green.
      showroomCandidacy:
        input.surfaces.length > 0 && rejectedSurfaces.length === 0
          ? "granted"
          : "denied",
    },
  };
}
